import type { VerdaInstanceType, VerdaLocationAvailability } from '../../../types/models/Verda';
import { VerdaInstanceKind, matchesInstanceFilters } from './verdaInstanceTypes';

// Pure helpers behind the Availability tab: the instance-type × location matrix and its filters.

export interface VerdaAvailabilityCell {
  onDemand: boolean;
  spot: boolean;
}

export interface VerdaAvailabilityRow {
  code: string;
  /** The catalog entry, or `null` for a code missing from the catalog (or when the catalog failed to load). */
  type: VerdaInstanceType | null;
  /** One cell per location, in the availability response's order. */
  cells: VerdaAvailabilityCell[];
  /** Deployable somewhere, on demand or as spot. */
  availableAnywhere: boolean;
}

/**
 * One row per catalog type in the catalog's (Verda's) order, then any code the snapshot lists that the
 * catalog doesn't know, in first-seen order — such a code still gets a row, with just its code.
 */
export function buildAvailabilityRows(
  availability: VerdaLocationAvailability[],
  catalog: VerdaInstanceType[] | null,
): VerdaAvailabilityRow[] {
  const sets = availability.map((location) => ({
    onDemand: new Set(location.on_demand),
    spot: new Set(location.spot),
  }));
  const toRow = (code: string, type: VerdaInstanceType | null): VerdaAvailabilityRow => {
    const cells = sets.map((set) => ({ onDemand: set.onDemand.has(code), spot: set.spot.has(code) }));
    return { code, type, cells, availableAnywhere: cells.some((cell) => cell.onDemand || cell.spot) };
  };

  const rows = (catalog ?? []).map((type) => toRow(type.instance_type, type));
  const seen = new Set(rows.map((row) => row.code));
  for (const location of availability) {
    for (const code of [...location.on_demand, ...location.spot]) {
      if (!seen.has(code)) {
        seen.add(code);
        rows.push(toRow(code, null));
      }
    }
  }
  return rows;
}

/** The Instance types tab's search + kind filter, plus "Only available" (hide rows available nowhere). */
export function filterAvailabilityRows(
  rows: VerdaAvailabilityRow[],
  query: string,
  kind: VerdaInstanceKind,
  onlyAvailable: boolean,
): VerdaAvailabilityRow[] {
  return rows.filter(
    (row) => (!onlyAvailable || row.availableAnywhere) && matchesInstanceFilters(row.code, row.type, query, kind),
  );
}
